/**
 * /terceirizacao/remessas — material em poder de terceiro.
 *
 * Bloco 8 do ADR-0061. A OSE já dizia QUE trabalho foi terceirizado. O
 * que faltava era o material: quantas chapas foram para o laqueador,
 * quantas voltaram e quantas ele quebrou. Isso vivia em dois JSON de
 * texto livre que ninguém somava.
 *
 * O material continua sendo nosso enquanto está lá fora — por isso a
 * primeira coisa da tela é quanto vale o que está na mão de cada um.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCornerDownLeft,
  FiRefreshCw,
  FiSend,
} from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { listarProdutos } from "../../../services/estoque";
import {
  criarRemessa,
  listarParceiros,
  listarRemessas,
  obterMaterialEmTerceiros,
  registrarRetornoRemessa,
  SITUACAO_REMESSA_LABEL,
  SITUACAO_REMESSA_TONE,
} from "../../../services/terceirizacao";
import { PageContainer } from "../../../styles/shared";
import { formatarQuantidade, formatarReaisBR } from "../../../utils/dinheiro";

import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Field,
  Form,
  FormFooter,
  InlineHint,
  Input,
  Pill,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
  Textarea,
} from "../../Orcamentos/styles";

const fmtData = (v) => (v ? new Date(v).toLocaleDateString("pt-BR") : "—");

export default function Remessas() {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const [remessas, setRemessas] = useState([]);
  const [painel, setPainel] = useState({ parceiros: [], valor_total: "0" });
  const [parceiros, setParceiros] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [nova, setNova] = useState(null);
  const [retorno, setRetorno] = useState(null);

  const podeEnviar = isMaster || hasAllPermissions(["estoque:transferir"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [lista, agregado] = await Promise.all([
        listarRemessas(request),
        obterMaterialEmTerceiros(request),
      ]);
      setRemessas(Array.isArray(lista) ? lista : lista?.results || []);
      setPainel(agregado || { parceiros: [], valor_total: "0" });
      setErro(null);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar remessas.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    listarParceiros(request, { ativo: true })
      .then((p) => setParceiros(Array.isArray(p) ? p : p?.results || []))
      .catch(() => setParceiros([]));
    listarProdutos(request, { pageSize: 500, ativo: true })
      .then((p) => setProdutos(Array.isArray(p) ? p : p?.results || []))
      .catch(() => setProdutos([]));
  }, [request]);

  const kpis = useMemo(() => {
    const abertas = remessas.filter((r) => r.situacao !== "RETORNADA").length;
    return [
      { label: "Valor lá fora", value: formatarReaisBR(painel.valor_total) },
      { label: "Parceiros com material", value: String(painel.parceiros.length) },
      { label: "Remessas em aberto", value: String(abertas) },
      { label: "Remessas no total", value: String(remessas.length) },
    ];
  }, [painel, remessas]);

  const handleEnviar = async (e) => {
    e.preventDefault();
    const itens = nova.itens.filter((i) => i.produto && Number(i.quantidade) > 0);
    if (!nova.parceiro || itens.length === 0) {
      setErro("Escolha o parceiro e ao menos um material com quantidade.");
      return;
    }
    setErro(null);
    try {
      await criarRemessa(request, {
        parceiro: nova.parceiro,
        observacao: nova.observacao || "",
        itens: itens.map((i) => ({
          produto: i.produto,
          quantidade: i.quantidade,
        })),
      });
      setNova(null);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao enviar material.");
    }
  };

  const handleRetorno = async (e) => {
    e.preventDefault();
    const linhas = retorno.linhas
      .filter((l) => Number(l.retornada) > 0 || Number(l.perdida) > 0)
      .map((l) => ({
        item: l.item,
        retornada: l.retornada || "0",
        perdida: l.perdida || "0",
      }));
    if (linhas.length === 0) {
      setErro("Informe o que voltou ou o que se perdeu.");
      return;
    }
    setErro(null);
    try {
      await registrarRetornoRemessa(request, retorno.remessa.id, linhas);
      setRetorno(null);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao registrar retorno.");
    }
  };

  return (
    <PageContainer>
      <EntityHeader
        title="Material em poder de terceiros"
        subtitle="O que saiu da fábrica e ainda é nosso"
        primaryAction={
          podeEnviar
            ? {
                label: "Enviar material",
                icon: <FiSend size={14} />,
                onClick: () => setNova({
                  parceiro: "", observacao: "",
                  itens: [{ produto: "", quantidade: "" }],
                }),
              }
            : undefined
        }
        actions={[
          { label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar },
        ]}
      />

      <KpiStrip items={kpis} />

      {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}

      <Card>
        <SectionTitle>Quanto cada parceiro tem de material nosso</SectionTitle>
        {painel.parceiros.length === 0 ? (
          <EmptyMsg>Nenhum material fora da fábrica.</EmptyMsg>
        ) : (
          <TableWrapper>
            <Table>
              <colgroup>
                <col style={{ width: "34%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "22%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Parceiro</th>
                  <th>Itens lá fora</th>
                  <th>Valor estimado</th>
                  <th>Mais antigo</th>
                </tr>
              </thead>
              <tbody>
                {painel.parceiros.map((p) => {
                  const antigo = p.itens
                    .map((i) => i.enviada_em)
                    .sort()[0];
                  return (
                    <tr key={p.parceiro_id}>
                      <td>{p.parceiro_nome}</td>
                      <td>{p.itens.length}</td>
                      <td>{formatarReaisBR(p.valor_estimado)}</td>
                      <td>{fmtData(antigo)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <Card>
        <SectionTitle>Remessas</SectionTitle>
        {loading ? (
          <EmptyMsg>Carregando...</EmptyMsg>
        ) : remessas.length === 0 ? (
          <EmptyMsg>Nenhuma remessa registrada.</EmptyMsg>
        ) : (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Parceiro</th>
                  <th>Enviada</th>
                  <th>Itens</th>
                  <th>Situação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {remessas.map((r) => (
                  <tr key={r.id}>
                    <td>{r.numero}</td>
                    <td>{r.parceiro_nome}</td>
                    <td>{fmtData(r.enviada_em)}</td>
                    <td>{(r.itens || []).length}</td>
                    <td>
                      <Pill $tone={SITUACAO_REMESSA_TONE[r.situacao]}>
                        {SITUACAO_REMESSA_LABEL[r.situacao] || r.situacao}
                      </Pill>
                    </td>
                    <td>
                      {podeEnviar && r.situacao !== "RETORNADA" && (
                        <SecondaryButton
                          type="button"
                          onClick={() => setRetorno({
                            remessa: r,
                            linhas: (r.itens || []).map((i) => ({
                              item: i.id,
                              codigo: i.produto_codigo,
                              pendente: i.quantidade_pendente,
                              retornada: "",
                              perdida: "",
                            })),
                          })}
                        >
                          <FiCornerDownLeft size={13} /> Registrar retorno
                        </SecondaryButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <SlideOverPanel
        open={Boolean(nova)}
        onClose={() => setNova(null)}
        title="Enviar material a terceiro"
      >
        {nova && (
          <Form onSubmit={handleEnviar}>
            <InlineHint>
              O material não sai do estoque: ele passa para o depósito do
              parceiro e continua contado como nosso.
            </InlineHint>
            <Field>
              <label>Parceiro *</label>
              <Select
                value={nova.parceiro}
                onChange={(e) => setNova((p) => ({ ...p, parceiro: e.target.value }))}
              >
                <option value="">Selecione...</option>
                {parceiros.map((p) => (
                  <option key={p.id} value={p.id}>{p.nome}</option>
                ))}
              </Select>
            </Field>

            {nova.itens.map((item, idx) => (
              <div key={idx} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <Field style={{ flex: 3 }}>
                  <label>Material</label>
                  <Select
                    value={item.produto}
                    onChange={(e) => setNova((p) => {
                      const itens = [...p.itens];
                      itens[idx] = { ...itens[idx], produto: e.target.value };
                      return { ...p, itens };
                    })}
                  >
                    <option value="">Selecione...</option>
                    {produtos.map((pr) => (
                      <option key={pr.id} value={pr.id}>
                        {pr.codigo} — {pr.nome}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field style={{ flex: 1 }}>
                  <label>Qtd</label>
                  <Input
                    value={item.quantidade}
                    inputMode="decimal"
                    onChange={(e) => setNova((p) => {
                      const itens = [...p.itens];
                      itens[idx] = { ...itens[idx], quantidade: e.target.value };
                      return { ...p, itens };
                    })}
                  />
                </Field>
              </div>
            ))}

            <SecondaryButton
              type="button"
              onClick={() => setNova((p) => ({
                ...p, itens: [...p.itens, { produto: "", quantidade: "" }],
              }))}
            >
              + Outro material
            </SecondaryButton>

            <Field>
              <label>Observação</label>
              <Textarea
                rows={2}
                value={nova.observacao}
                onChange={(e) => setNova((p) => ({ ...p, observacao: e.target.value }))}
              />
            </Field>

            {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setNova(null)}>
                Cancelar
              </SecondaryButton>
              <PrimaryButton type="submit">Enviar material</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>

      <SlideOverPanel
        open={Boolean(retorno)}
        onClose={() => setRetorno(null)}
        title={retorno ? `Retorno da ${retorno.remessa.numero}` : "Retorno"}
      >
        {retorno && (
          <Form onSubmit={handleRetorno}>
            <InlineHint>
              O que voltou entra de novo no galpão. O que se perdeu sai do
              saldo com o motivo &quot;perda em poder de terceiro&quot; — é
              assim que se descobre qual parceiro custa mais em material.
            </InlineHint>
            {retorno.linhas.map((l, idx) => (
              <div key={l.item} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: "0.8rem", marginBottom: 4 }}>
                  <strong>{l.codigo}</strong> — pendente com o parceiro:{" "}
                  {formatarQuantidade(l.pendente)}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Field style={{ flex: 1 }}>
                    <label>Voltou</label>
                    <Input
                      value={l.retornada}
                      inputMode="decimal"
                      onChange={(e) => setRetorno((p) => {
                        const linhas = [...p.linhas];
                        linhas[idx] = { ...linhas[idx], retornada: e.target.value };
                        return { ...p, linhas };
                      })}
                    />
                  </Field>
                  <Field style={{ flex: 1 }}>
                    <label>Perdido / quebrado</label>
                    <Input
                      value={l.perdida}
                      inputMode="decimal"
                      onChange={(e) => setRetorno((p) => {
                        const linhas = [...p.linhas];
                        linhas[idx] = { ...linhas[idx], perdida: e.target.value };
                        return { ...p, linhas };
                      })}
                    />
                  </Field>
                </div>
              </div>
            ))}
            {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setRetorno(null)}>
                Cancelar
              </SecondaryButton>
              <PrimaryButton type="submit">Registrar retorno</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>
    </PageContainer>
  );
}
